import { ReactNode } from 'react';
import { Button, cn } from '@qodo/design-system';
import { Check, LoaderCircle, TriangleAlert } from 'lucide-react';

export type InstallationStatusMessageProps = {
  status: 'success' | 'error' | 'warning' | 'pending';
  message: ReactNode;
  detail?: string;
  retryLabel?: string;
  onRetry?: () => void;
  isRetrying?: boolean;
};

export const InstallationStatusMessage = ({
  status,
  message,
  detail,
  retryLabel,
  onRetry,
  isRetrying = false,
}: InstallationStatusMessageProps) => {
  const isSuccess = status === 'success';
  const isWarning = status === 'warning';
  const isPending = status === 'pending';

  const containerClass = isSuccess
    ? 'border-border-tag-positive-idle text-text-tag-positive-active'
    : isWarning
      ? 'border-border-tag-warning-idle text-text-tag-warning-active'
      : isPending
        ? 'border-border-tag-neutral-idle text-text-tag-neutral-active'
        : 'border-border-tag-negative-idle text-text-tag-negative-active';

  const iconClass = isSuccess
    ? 'size-4 stroke-icon-tag-positive-active'
    : isWarning
      ? 'size-4 stroke-icon-tag-warning-active'
      : isPending
        ? 'size-4 stroke-icon-tag-neutral-active'
        : 'size-4 stroke-icon-tag-negative-active';

  const renderIcon = () => {
    if (isPending || (isRetrying && !isSuccess)) {
      return <LoaderCircle className={cn('animate-spin', iconClass)} />;
    }

    if (isSuccess) {
      return <Check className={iconClass} />;
    }

    return <TriangleAlert className={iconClass} />;
  };

  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 p-4 rounded-lg border',
        containerClass,
      )}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2.5">
          {renderIcon()}
          {message}
        </div>
        {detail && (
          <p className="text-text-bg-neutral-mild text-xs leading-normal tracking-[0.12px]">
            {detail}
          </p>
        )}
      </div>

      {retryLabel && onRetry && (
        <Button
          type="button"
          variant="outline"
          size="xs"
          disabled={isRetrying}
          onClick={onRetry}
          className="shrink-0 px-2 py-1.5 flex gap-1.5 text-text-interactive-neutral-idle"
        >
          {isRetrying && (
            <LoaderCircle className="animate-spin size-4 stroke-icon-action-neutral-disabled" />
          )}
          {retryLabel}
        </Button>
      )}
    </div>
  );
};
